import { useDispatch } from 'react-redux'
import { PaymentRequest } from '@stripe/stripe-js'
import { setCanMakePayment, setWalletType } from 'redux/features/stripePaymentRequest'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import useOrder from 'modules/order/state/order/use-order'
import getStripePromise from './getStripePromise'
import { convertUSDToCents } from 'utils/currency'
import { useRef } from 'react'
import useConfig from 'modules/config/state/use-config'
import { useTranslation } from 'next-i18next'

let stripePaymentRequest: PaymentRequest | null = null

/**
 * Returns a promise that resolves with the stripe payment request and initializes digital wallets
 */
const useStripePaymentRequest = async () => {
  const { t, i18n } = useTranslation()
  const config = useConfig()
  const order = useOrder()
  const dispatch = useDispatch()
  const stripePromise = getStripePromise(dispatch, i18n.language)

  /**
   * Determine the correct amount to send to Stripe based on DCF state.
   *
   * If DCF is enabled and checked by the donor, always use the total amount with fees.
   * This ensures that Stripe shows the correct amount that the donor has agreed to pay.
   *
   * If dcfNudge.enabled = true, donor has already seen a dcf checkbox in the flow
   * and dcf.value.totalAmount will show the amount with or without DCF based on their choice.
   *
   * If dcfNudge.enabled = false but DCF is still enabled (e.g., in single-step checkout),
   * the donor can still see and interact with the DCF checkbox. In this case, we should
   * respect their choice and use totalAmount if they've checked DCF.
   *
   * Only use the base amount if DCF is not enabled or not checked by the donor.
   */
  const amount =
    config.dcfNudge.enabled || (config.dcf.isEnabled && order.dcf.value.isChecked)
      ? convertUSDToCents(order.dcf.value.totalAmount)
      : convertUSDToCents(order.amount.value)

  // Track whether the event has been fired
  const dwLoadedRef = useRef(false)

  if (!amount || !stripePromise) {
    return null // Exit early if no amount or Stripe promise
  }

  // Return the existing payment request if it's already initialized
  if (stripePaymentRequest) {
    if (!stripePaymentRequest.isShowing()) {
      stripePaymentRequest.update({
        total: {
          amount,
          label: t('useStripePaymentRequest.label.donation', 'Donation'),
        },
      })
    }

    return stripePaymentRequest
  }

  return stripePromise.then((stripe) => {
    if (!stripe || !amount) {
      return null
    }

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        amount,
        label: t('useStripePaymentRequest.label.donation', 'Donation'),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    })

    return pr.canMakePayment().then((canMakePayment) => {
      const dwLoaded = !!canMakePayment?.applePay || !!canMakePayment?.googlePay

      const walletType = canMakePayment?.applePay
        ? 'APPLEPAY'
        : canMakePayment?.googlePay
        ? 'GPAY'
        : null

      dispatch(setWalletType(walletType))
      dispatch(setCanMakePayment(dwLoaded))

      // Only track the "Digital Wallets Loaded" event once
      if (!dwLoadedRef.current) {
        const analytics = getAnalyticsInstance()

        analytics.track(
          'Digital Wallets Loaded',
          { digitalWalletsLoaded: dwLoaded },
          {
            plugins: {
              all: false,
              heap: true,
            },
          },
        )

        dwLoadedRef.current = true // Mark that the event has been fired
      }

      stripePaymentRequest = pr // Cache the initialized request

      return stripePaymentRequest
    })
  })
}

export default useStripePaymentRequest
